import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { v7 as uuidv7 } from "uuid";
import { FormContainer, NarrowContainer } from "../../components/Container";
import { Title } from "../../components/Title";
import { Divider } from "../../components/Divider";
import {
  SubmitButton,
  BackButton,
  SwitchButton,
} from "../../components/Button";
import { TextInput, CurrencyInput } from "../../components/TextInput";
import { SelectBox } from "../../components/SelectBox";
import UserUtils from "../../util/UserUtils";
import {
  getProducts,
  getCalculatedDiscounts,
  createOrder,
  getOrderById,
  updateOrder,
  updateOrderHistory,
  getMarketingSources,
} from "../../api/AWSAPI";
import moment from "moment";

// Contains brands visible in the order form.
const BRANDS = Object.freeze({
  ARGOS: "argos",
  SAINSBURYS: "sainsburys",
  HABITAT: "habitat",
  MULTIBRAND: "multibrand",
});

const OTHER_BRAND = "other";

// Brands that are aggregated individually; anything else is grouped as "other".
const AGGREGATABLE_BRANDS = new Set(Object.values(BRANDS));

/**
 * Normalises a brand string to a known key, or OTHER_BRAND if unrecognised.
 * @param {string} brand
 * @returns {string} normalised brand key
 */
const normaliseBrand = (brand) => {
  const key = (brand || "").toLowerCase();
  return AGGREGATABLE_BRANDS.has(key) ? key : OTHER_BRAND;
};

/**
 * Aggregates order items by brand and sums their line totals (quantity * value).
 * @param {Array<{brand?: string, qty: string|number, value: string|number}>} items
 * @returns {Record<string, number>} brand -> total amount
 */
const aggregateOrderItems = (items = []) =>
  items.reduce((totals, { brand, qty, value }) => {
    const key = normaliseBrand(brand);
    const lineTotal = toNumber(qty) * toNumber(value);
    totals[key] = totals[key] ? totals[key] + lineTotal : lineTotal;
    return totals;
  }, {});

// ---------------------------------------------------------------------------
// Discount helpers (pure – no component state)
// ---------------------------------------------------------------------------

const MAX_DISCOUNT_RATE = 10;
const DISCOUNT_RATE_ERROR = `Discount rate cannot exceed ${MAX_DISCOUNT_RATE}%`;

// Display/config metadata for each discountable brand, in UI order.
const DISCOUNT_BRAND_CONFIG = [
  { brand: BRANDS.ARGOS, label: "Argos", fieldName: "argosDiscountRate" },
  { brand: BRANDS.SAINSBURYS, label: "Sainsbury's", fieldName: "sainsburysDiscountRate" },
  { brand: BRANDS.HABITAT, label: "Habitat", fieldName: "habitatDiscountRate" },
  { brand: BRANDS.MULTIBRAND, label: "Multi Brand", fieldName: "multibrandDiscountRate" },
];

const roundCurrency = (n) => Number(Math.round(n + "e2") + "e-2");

// Rates may be raw input strings (e.g. "5.") or numbers; always compute with numbers.
const toNumber = (v) => Number.parseFloat(v) || 0;

const isDiscountRateTooHigh = (discountRate) => toNumber(discountRate) > MAX_DISCOUNT_RATE;

// Discount amount for a brand total at the given percentage rate (rate rounded to 2dp).
const calcDiscountFromRate = (brandTotal, discountRate) =>
  roundCurrency((toNumber(brandTotal) / 100) * roundCurrency(toNumber(discountRate)));

// Normalises the discount API response into { [brand]: { total, rate, amount } }.
const parseDiscountItems = (fetchedDiscountItems = []) => {
  const result = Object.fromEntries(
    DISCOUNT_BRAND_CONFIG.map(({ brand }) => [
      brand,
      { total: 0, discountRate: 0, discountAmount: 0 },
    ]),
  );

  fetchedDiscountItems.forEach(({ brand, amount, discountRate, discountAmount }) => {
    if (result[brand]) {
      result[brand] = {
        total: Number.parseFloat(amount) || 0,
        discountRate: Number.parseFloat(discountRate) || 0,
        discountAmount: Number.parseFloat(discountAmount) || 0,
      };
    }
  });
  return result;
};

// Order API operations used when building order lines.
const ORDER_OPERATION = Object.freeze({
  CREATE: "create",
  UPDATE: "update",
});

/**
 * Builds API order lines from order items (pure – no component state).
 * Discount fields (discountRate, discountOverride) are only included for the
 * update operation; omitted for create.
 * @param {"create"|"update"} operation - ORDER_OPERATION value
 * @param {Array<object>} items - order items from the form
 * @param {Array<object>} productList - loaded products (for deliveryType fallback)
 * @param {string} orderNumber
 * @param {Record<string, {discountRate: string|number, isDiscountOverridden: boolean}>} brandDiscounts
 * @returns {Array<object>} order lines
 */
const buildOrderLines = (operation, items, productList, orderNumber, brandDiscounts) =>
  items
    .filter((item) => item.itemType !== 99)
    .map((item) => {
      const line = {
        productId: item.productId,
        lineId: item.lineId ?? item.ID,
        productBrand: item.brand,
        productName: item.itemType,
        quantity: Number.parseInt(item.qty),
        typeFixedOrVariable: item.typeFixedOrVariable,
        amount: item.value,
        addedDelivery: item.addedDelivery,
        addedWallets: item.addedWallets,
        productType: item.productType,
        deliveryType:
          item.deliveryType ||
          productList.find((p) => p.value === item.productId)?.deliveryType,
        orderNumber,
      };
      if (operation === ORDER_OPERATION.CREATE) return line;

      const brandState = Object.hasOwn(brandDiscounts, item.brand)
        ? brandDiscounts[item.brand]
        : null;
      return {
        ...line,
        discountRate: brandState ? toNumber(brandState.discountRate) : 0,
        discountOverride: brandState ? Boolean(brandState.isDiscountOverridden) : false,
      };
    });

function OrderEditPage({ navigation }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const search = useLocation().search;

  const _copy = new URLSearchParams(search).get("copy");

  /*
   * Below two state variables are used to handle company or charity ID
   * and marketing opt-out status which will not be visible in the UI but
   * are required for order creation and replication.
   *
   * For new orders:
   *   - companyOrCharityID will be empty string
   *   - marketingOptOut will default to true
   *
   * For existing orders:
   *   - these values will be loaded from the order data
   */
  const [companyOrCharityID, setCompanyOrCharityID] = useState("");
  const [marketingOptOut, setMarketingOptOut] = useState(true);

  const [clientName, setClientName] = useState("");
  const [clientPO, setClientPO] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactLine1, setContactLine1] = useState("");
  const [contactLine2, setContactLine2] = useState("");
  const [contactTown, setContactTown] = useState("");
  const [contactPostcode, setContactPostcode] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientCompany, setRecipientCompany] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientLine1, setRecipientLine1] = useState("");
  const [recipientLine2, setRecipientLine2] = useState("");
  const [recipientTown, setRecipientTown] = useState("");
  const [recipientPostcode, setRecipientPostcode] = useState("");
  const [recipientTel, setRecipientTel] = useState("");
  const [contactTel, setContactTel] = useState("");
  const [type, setType] = useState("");
  const [isCardValueVisible, setIsCardValueVisible] = useState(false);
  const [qty, setQty] = useState(0);
  const [value, setValue] = useState(0);
  const [copyAddress, setCopyAddress] = useState(false);
  const [orderItems, setOrderItems] = useState([]);
  const [errClientName, seterrClientName] = useState("");
  const [errClientPO, seterrClientPO] = useState("");
  const [errContactName, seterrContactName] = useState("");
  const [errContactEmail, seterrContactEmail] = useState("");
  const [errContactLine1, seterrContactLine1] = useState("");
  const [errContactLine2, seterrContactLine2] = useState("");
  const [errContactTown, seterrContactTown] = useState("");
  const [errContactPostcode, seterrContactPostcode] = useState("");
  const [errRecipientName, seterrRecipientName] = useState("");
  const [errRecipientEmail, seterrRecipientEmail] = useState("");
  const [errRecipientLine1, seterrRecipientLine1] = useState("");
  const [errRecipientLine2, seterrRecipientLine2] = useState("");
  const [errRecipientTown, seterrRecipientTown] = useState("");
  const [errRecipientPostcode, seterrRecipientPostcode] = useState("");
  const [errRecipientTel, seterrRecipientTel] = useState("");
  const [errContactTel, seterrContactTel] = useState("");
  const [errOrderItems, seterrOrderItems] = useState("");
  const [contactCountry, setContactCountry] = useState("UK");
  const [recipientCountry, setRecipientCountry] = useState("UK");
  const [errContactCountry, seterrContactCountry] = useState("");
  const [errRecipientCompany, seterrRecipientCompany] = useState("");
  const [errRecipientCountry, seterrRecipientCountry] = useState("");
  const [howDidYouHear, setHowDidYouHear] = useState("");
  const [errHowDidYouHear, setErrHowDidYouHear] = useState("");
  const [howDidYouHearOther, setHowDidYouHearOther] = useState("");
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [saving, setsaving] = useState(false);
  const [isCardPaymentsEnable, setIsCardPaymentsEnable] = useState(true);
  const [hasErrors, setHasErrors] = useState(false);
  const [products, setproducts] = useState([]);
  const [marketingSources, setMarketingSources] = useState([]);
  const [allowCardPayments, setAllowCardPayments] = useState(false);
  const [apiError, setApiError] = useState("");
  const [confirmedByName, setConfirmedByName] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [originalOrderData, setOriginalOrderData] = useState({});
  const [orderItemsChanged, setOrderItemsChanged] = useState(false);
  const [totalDeliveryAmount, setTotalDeliveryAmount] = useState(0);
  const [deliveryChargeOverridden, setDeliveryChargeOverridden] =
    useState(false);
  const [loadedHearAboutUs, setLoadedHearAboutUs] = useState(null);
  const [isDiscountLoading, setIsDiscountLoading] = useState(false);

  // ---------------------------------------------------------------------------
  // Per-brand discount state
  //   <brand>DiscountTotal       – summed order value for the brand
  //   <brand>DiscountRate        – applied discount percentage
  //   <brand>DiscountAmount      – discount value in £
  //   <brand>DiscountRateError   – validation message for the rate input
  //   is<Brand>DiscountEditable  – rate input enabled (brand present in order)
  //   is<Brand>CustomDiscount    – user typed a rate (takes precedence over API rate)
  //   is<Brand>DiscountOverridden – rate manually overridden (persisted on save)
  // ---------------------------------------------------------------------------

  // Argos
  const [argosDiscountTotal, setArgosDiscountTotal] = useState(0);
  const [argosDiscountRate, setArgosDiscountRate] = useState(0);
  const [argosDiscountAmount, setArgosDiscountAmount] = useState(0);
  const [argosDiscountRateError, setArgosDiscountRateError] = useState("");
  const [isArgosDiscountEditable, setIsArgosDiscountEditable] = useState(false);
  const [isArgosCustomDiscount, setIsArgosCustomDiscount] = useState(false);
  const [isArgosDiscountOverridden, setIsArgosDiscountOverridden] =
    useState(false);

  // Sainsbury's
  const [sainsburysDiscountTotal, setSainsburysDiscountTotal] = useState(0);
  const [sainsburysDiscountRate, setSainsburysDiscountRate] = useState(0);
  const [sainsburysDiscountAmount, setSainsburysDiscountAmount] = useState(0);
  const [sainsburysDiscountRateError, setSainsburysDiscountRateError] =
    useState("");
  const [isSainsburysDiscountEditable, setIsSainsburysDiscountEditable] =
    useState(false);
  const [isSainsburysCustomDiscount, setIsSainsburysCustomDiscount] =
    useState(false);
  const [isSainsburysDiscountOverridden, setIsSainsburysDiscountOverridden] =
    useState(false);

  // Habitat
  const [habitatDiscountTotal, setHabitatDiscountTotal] = useState(0);
  const [habitatDiscountRate, setHabitatDiscountRate] = useState(0);
  const [habitatDiscountAmount, setHabitatDiscountAmount] = useState(0);
  const [habitatDiscountRateError, setHabitatDiscountRateError] = useState("");
  const [isHabitatDiscountEditable, setIsHabitatDiscountEditable] =
    useState(false);
  const [isHabitatCustomDiscount, setIsHabitatCustomDiscount] = useState(false);
  const [isHabitatDiscountOverridden, setIsHabitatDiscountOverridden] =
    useState(false);

  // Multi brand
  const [multibrandDiscountTotal, setMultibrandDiscountTotal] = useState(0);
  const [multibrandDiscountRate, setMultibrandDiscountRate] = useState(0);
  const [multibrandDiscountAmount, setMultibrandDiscountAmount] = useState(0);
  const [multibrandDiscountRateError, setMultibrandDiscountRateError] =
    useState("");
  const [isMultibrandDiscountEditable, setIsMultibrandDiscountEditable] =
    useState(false);
  const [isMultibrandCustomDiscount, setIsMultibrandCustomDiscount] =
    useState(false);
  const [isMultibrandDiscountOverridden, setIsMultibrandDiscountOverridden] =
    useState(false);

  useEffect(() => {
    setHasErrors(false);
    seterrClientName("");
    seterrClientPO("");
    seterrContactName("");
    seterrContactEmail("");
    seterrContactLine1("");
    seterrContactLine2("");
    seterrContactTown("");
    seterrContactPostcode("");
    seterrRecipientEmail("");
    seterrRecipientName("");
    seterrRecipientLine1("");
    seterrRecipientLine2("");
    seterrRecipientTown("");
    seterrRecipientPostcode("");
    seterrRecipientTel("");
    seterrContactTel("");
    seterrOrderItems("");
    seterrRecipientCountry("");
    setArgosDiscountRateError("");
    setSainsburysDiscountRateError("");
    setHabitatDiscountRateError("");
    setMultibrandDiscountRateError("");
    seterrContactCountry("");
    setErrHowDidYouHear("");
    setApiError("");
  }, [
    clientName,
    clientPO,
    contactName,
    contactEmail,
    contactLine1,
    contactLine2,
    contactTown,
    contactPostcode,
    recipientEmail,
    recipientName,
    recipientLine1,
    recipientLine2,
    recipientTown,
    recipientPostcode,
    recipientTel,
    contactTel,
    orderItems,
    recipientCountry,
    argosDiscountRate,
    sainsburysDiscountRate,
    habitatDiscountRate,
    multibrandDiscountRate,
    contactCountry,
    copyAddress,
    howDidYouHear,
    howDidYouHearOther,
  ]);

  useEffect(() => {
    UserUtils.getUserData((data) => {
      if (data) {
        setConfirmedByName(`${data.name || ""}`.trim());
      } else {
        navigate("/");
      }
    });
    loadProducts();
    loadMarketingSources();
    if (id !== "new") {
      loadOrder(id);
    } else if (_copy) {
      loadOrder(_copy);
    }
  }, []);

  // Process hearAboutUs once marketingSources is loaded
  useEffect(() => {
    if (loadedHearAboutUs && marketingSources.length > 0) {
      const matchingSource = marketingSources.find(
        (source) => source.value === loadedHearAboutUs,
      );

      if (matchingSource) {
        setHowDidYouHear(loadedHearAboutUs);
        if (loadedHearAboutUs === "other") {
          setShowOtherInput(true);
          setHowDidYouHearOther("");
        } else {
          setShowOtherInput(false);
        }
      } else {
        setHowDidYouHear("other");
        setShowOtherInput(true);
        setHowDidYouHearOther(loadedHearAboutUs);
      }
      setLoadedHearAboutUs(null);
    }
  }, [loadedHearAboutUs, marketingSources]);

  const replaceWordChars = (text) => {
    let s = text;
    // smart single quotes and apostrophe
    s = s.replace(/[\u2018|\u2019|\u201A]/g, "'");
    // smart double quotes
    s = s.replace(/[\u201C|\u201D|\u201E]/g, '"');
    // ellipsis
    s = s.replace(/\u2026/g, "...");
    // dashes
    s = s.replace(/[\u2013|\u2014]/g, "-");
    // circumflex
    s = s.replace(/\u02C6/g, "^");
    // open angle bracket
    s = s.replace(/\u2039/g, "<");
    // close angle bracket
    s = s.replace(/\u203A/g, ">");
    // spaces
    s = s.replace(/[\u02DC|\u00A0]/g, " ");
    return s;
  };

  // Per-brand state + setters, so discount logic can iterate instead of repeating itself.
  const brandDiscountState = {
    [BRANDS.ARGOS]: {
      discountRate: argosDiscountRate,
      discountTotal: argosDiscountTotal,
      discountAmount: argosDiscountAmount,
      isCustomDiscount: isArgosCustomDiscount,
      isDiscountOverridden: isArgosDiscountOverridden,
      isDiscountEditable: isArgosDiscountEditable,
      discountRateError: argosDiscountRateError,
      setDiscountTotal: setArgosDiscountTotal,
      setDiscountRate: setArgosDiscountRate,
      setDiscountAmount: setArgosDiscountAmount,
      setIsDiscountEditable: setIsArgosDiscountEditable,
      setIsCustomDiscount: setIsArgosCustomDiscount,
      setIsDiscountOverridden: setIsArgosDiscountOverridden,
      setDiscountRateError: setArgosDiscountRateError,
    },
    [BRANDS.SAINSBURYS]: {
      discountRate: sainsburysDiscountRate,
      discountTotal: sainsburysDiscountTotal,
      discountAmount: sainsburysDiscountAmount,
      isCustomDiscount: isSainsburysCustomDiscount,
      isDiscountOverridden: isSainsburysDiscountOverridden,
      isDiscountEditable: isSainsburysDiscountEditable,
      discountRateError: sainsburysDiscountRateError,
      setDiscountTotal: setSainsburysDiscountTotal,
      setDiscountRate: setSainsburysDiscountRate,
      setDiscountAmount: setSainsburysDiscountAmount,
      setIsDiscountEditable: setIsSainsburysDiscountEditable,
      setIsCustomDiscount: setIsSainsburysCustomDiscount,
      setIsDiscountOverridden: setIsSainsburysDiscountOverridden,
      setDiscountRateError: setSainsburysDiscountRateError,
    },
    [BRANDS.HABITAT]: {
      discountRate: habitatDiscountRate,
      discountTotal: habitatDiscountTotal,
      discountAmount: habitatDiscountAmount,
      isCustomDiscount: isHabitatCustomDiscount,
      isDiscountOverridden: isHabitatDiscountOverridden,
      isDiscountEditable: isHabitatDiscountEditable,
      discountRateError: habitatDiscountRateError,
      setDiscountTotal: setHabitatDiscountTotal,
      setDiscountRate: setHabitatDiscountRate,
      setDiscountAmount: setHabitatDiscountAmount,
      setIsDiscountEditable: setIsHabitatDiscountEditable,
      setIsCustomDiscount: setIsHabitatCustomDiscount,
      setIsDiscountOverridden: setIsHabitatDiscountOverridden,
      setDiscountRateError: setHabitatDiscountRateError,
    },
    [BRANDS.MULTIBRAND]: {
      discountRate: multibrandDiscountRate,
      discountTotal: multibrandDiscountTotal,
      discountAmount: multibrandDiscountAmount,
      isCustomDiscount: isMultibrandCustomDiscount,
      isDiscountOverridden: isMultibrandDiscountOverridden,
      isDiscountEditable: isMultibrandDiscountEditable,
      discountRateError: multibrandDiscountRateError,
      setDiscountTotal: setMultibrandDiscountTotal,
      setDiscountRate: setMultibrandDiscountRate,
      setDiscountAmount: setMultibrandDiscountAmount,
      setIsDiscountEditable: setIsMultibrandDiscountEditable,
      setIsCustomDiscount: setIsMultibrandCustomDiscount,
      setIsDiscountOverridden: setIsMultibrandDiscountOverridden,
      setDiscountRateError: setMultibrandDiscountRateError,
    },
  };

  // Config (label, field name) merged with live state, in UI order.
  const discountBrands = DISCOUNT_BRAND_CONFIG.map((config) => ({
    ...config,
    ...brandDiscountState[config.brand],
  }));

  const CARD_PAYMENT_LIMIT = 2500;

  //totalCost = orderTotal - totalDiscountAmount
  // deliveryAmount is passed explicitly when it changes in the same handler (state would be stale).
  const updateCardPaymentAvailability = (totalCost, deliveryAmount = totalDeliveryAmount) => {
    console.log("updateCardPaymentAvailability", totalCost, deliveryAmount);
    const withinLimit = totalCost + toNumber(deliveryAmount) <= CARD_PAYMENT_LIMIT;
    setIsCardPaymentsEnable(withinLimit);
    if (!withinLimit) setAllowCardPayments(false);
  };

  const resetDiscounts = () => {
    Object.values(brandDiscountState).forEach(
      ({
        setDiscountTotal,
        setDiscountRate,
        setDiscountAmount,
        setIsDiscountEditable,
        setIsDiscountOverridden,
        setDiscountRateError,
        setIsCustomDiscount,
      }) => {
        setDiscountTotal(0);
        setDiscountRate(0);
        setDiscountAmount(0);
        setIsDiscountEditable(false);
        setIsDiscountOverridden(false);
        setDiscountRateError("");
        setIsCustomDiscount(false);
      },
    );
    setAllowCardPayments(false);
    setIsCardPaymentsEnable(true);
  };

  /**
   * Recalculates per-brand totals and discounts for the given order items.
   *
   * - Empty list: resets all brand state.
   * - Brands no longer in the order are reset.
   * - Overridden brands keep the user's rate (amount recalculated locally) and
   *   are excluded from the API request.
   * - Remaining brands get their rate/amount from the API.
   * - While the request is in flight, add/delete and rate inputs are locked,
   *   so only one request can exist at a time.
   *
   * @param {Array<{brand?: string, qty: string|number, value: string|number}>} newOrderList
   */
  const calculatedDiscounts = (newOrderList, deliveryAmount = totalDeliveryAmount) => {
    if (newOrderList.length === 0) {
      resetDiscounts();
      return;
    }

    const brandTotals = aggregateOrderItems(newOrderList);
    const isOverridden = (brand) =>
      brandDiscountState[brand].isCustomDiscount && (brandTotals[brand] ?? 0) > 0;

    Object.entries(brandDiscountState).forEach(
      ([
        brand,
        {
          setDiscountTotal,
          setDiscountRate,
          setDiscountAmount,
          setIsDiscountEditable,
          setIsCustomDiscount,
          setIsDiscountOverridden,
          setDiscountRateError,
        },
      ]) => {
        const brandTotal = brandTotals[brand] ?? 0;
        setDiscountTotal(brandTotal);
        setIsDiscountEditable(false);
        if (brandTotal <= 0) {
          setDiscountRate(0);
          setDiscountAmount(0);
          setIsCustomDiscount(false);
          setIsDiscountOverridden(false);
          setDiscountRateError("");
        }
      },
    );

    // Local cost for overridden brands (overridden rate applied to the new total).
    let overriddenCost = toNumber(brandTotals[OTHER_BRAND]);
    Object.entries(brandDiscountState).forEach(
      ([brand, { discountRate, setDiscountAmount }]) => {
        if (isOverridden(brand)) {
          const brandTotal = brandTotals[brand];
          const brandDiscountAmount = calcDiscountFromRate(brandTotal, discountRate);
          setDiscountAmount(brandDiscountAmount);
          overriddenCost += toNumber(brandTotal) - brandDiscountAmount;
        }
      },
    );

    const unlockDiscountInputs = () => {
      Object.entries(brandDiscountState).forEach(([brand, { setIsDiscountEditable }]) => {
        setIsDiscountEditable((brandTotals[brand] ?? 0) > 0);
      });
      setIsDiscountLoading(false);
    };

    const requestItems = Object.entries(brandTotals)
      .filter(([brand, amount]) => brand !== OTHER_BRAND && amount > 0 && !isOverridden(brand))
      .map(([brand, amount]) => ({ brand, amount: String(amount) }));

    if (requestItems.length === 0) {
      updateCardPaymentAvailability(overriddenCost, deliveryAmount);
      unlockDiscountInputs();
      return;
    }

    setIsDiscountLoading(true);

    getCalculatedDiscounts(requestItems, (responseBody) => {
      if (!responseBody?.items) {
        unlockDiscountInputs();
        return;
      }

      const parsedDiscountItems = parseDiscountItems(responseBody.items);
      let totalCost = overriddenCost;

      requestItems.forEach(({ brand }) => {
        const { setDiscountRate, setDiscountAmount } = brandDiscountState[brand];
        const { discountRate, discountAmount } = parsedDiscountItems[brand];
        setDiscountRate(discountRate);
        setDiscountAmount(discountAmount);
        totalCost += toNumber(brandTotals[brand]) - discountAmount;
      });

      updateCardPaymentAvailability(totalCost, deliveryAmount);
      unlockDiscountInputs();
    });
  };

  const loadProducts = () => {
    getProducts((responseBody) => {
      if (responseBody.success) {
        const products = responseBody.items;
        const productData = products
          .filter((product) => product.nagcoInclude === "Y")
          .reduce((accumulator, product) => {
            accumulator.push({
              value: product.id,
              label:
                product.productDisplayDescription ?? product.productDescription,
              typeFixedOrVariable: product.typeFixedOrVariable,
              amountFixed: product.amountFixed ?? 0,
              brand: product.brand,
              deliveryType: product.deliveryType,
              productType: product.productType,
              wallets: product.wallets ? JSON.parse(product.wallets) : [],
              addWallets: product.addWallets,
              maxLoad: product.maxLoad,
            });
            return accumulator;
          }, []);
        setproducts(productData.sort((a, b) => a.label.localeCompare(b.label)));
      } else {
        setproducts([]);
      }
    });
  };

  const loadMarketingSources = () => {
    getMarketingSources((responseBody) => {
      if (responseBody?.success && responseBody.items) {
        const sourcesData = responseBody.items.map((source) => ({
          value: source.sourceCode,
          label: source.sourceText,
        }));
        setMarketingSources(sourcesData);
      } else {
        setMarketingSources([]);
      }
    });
  };

  const loadOrder = (_id) => {
    getOrderById(_id, (data) => {
      if (data && data.success && data.order.length > 0) {
        const order = data.order[0];
        setClientName(order.contactCompany);
        setClientPO(order.clientPO);
        setContactName(order.contactName);
        setContactEmail(order.contactEmail);
        setContactLine1(order.contactLine1);
        setContactLine2(order.contactLine2);
        setContactTown(order.contactTown);
        setContactPostcode(order.contactPostcode);
        setOrderNumber(order.orderNumber);
        setRecipientName(order.recipientName);
        setRecipientEmail(order.recipientEmail);
        setRecipientLine1(order.recipientLine1);
        setRecipientLine2(order.recipientLine2);
        setRecipientTown(order.recipientTown);
        setRecipientPostcode(order.recipientPostcode);
        setCopyAddress(order.copyAddress);
        setContactTel(order.contactTel);
        setAllowCardPayments(order.allowCardPayments);
        setContactCountry(order.contactCountry);
        setRecipientCountry(order.recipientCountry);
        setRecipientTel(order.recipientTel);
        setRecipientCompany(order.recipientCompany);
        setTotalDeliveryAmount(order.totalDeliveryAmount);
        setLoadedHearAboutUs(order.hearAboutUs);

        const items = order.items.map((item) => ({
          ...item,
          ID: item.lineId,
          brand: item.productBrand,
          itemType: item.productName,
          qty: item.quantity.toString(),
          value: item.amount,
        }));
        setOrderItems(items);

        if(!order.discounts || Array.isArray(order.discounts) === false || !order.discounts.length) {
          const brands = new Set(items.map((item) => item.productBrand));
          brands.forEach((brand) => {
            if (!Object.hasOwn(brandDiscountState, brand)) return;
            const brandState = brandDiscountState[brand];
            brandState.setIsDiscountEditable(true);
          });
        } else {
          order.discounts?.forEach((item) => {
          if (!Object.hasOwn(brandDiscountState, item.productBrand)) return;
          const brandState = brandDiscountState[item.productBrand];
          brandState.setDiscountAmount(Number(item.discountAmount));
          brandState.setDiscountRate(Number(item.discountRate));
          brandState.setDiscountTotal(Number(item.brandAmountTotal));
          brandState.setIsDiscountOverridden(item.discountOverride);
          brandState.setIsCustomDiscount(Boolean(item.discountOverride));
          brandState.setIsDiscountEditable(Number(item.brandAmountTotal) > 0);
        });
        }
        

        setOriginalOrderData({
          clientPO: order.clientPO,
          contactName: order.contactName,
          contactLine1: order.contactLine1,
          contactLine2: order.contactLine2,
          contactTown: order.contactTown,
          contactPostcode: order.contactPostcode,
          contactCountry: order.contactCountry,
          contactEmail: order.contactEmail,
          contactTel: order.contactTel,
          copyAddress: order.copyAddress,
          recipientCompany: order.recipientCompany,
          recipientName: order.recipientName,
          recipientEmail: order.recipientEmail,
          recipientLine1: order.recipientLine1,
          recipientLine2: order.recipientLine2,
          recipientTown: order.recipientTown,
          recipientPostcode: order.recipientPostcode,
          recipientCountry: order.recipientCountry,
          recipientTel: order.recipientTel,
          allowCardPayments: order.allowCardPayments,
          contactCompany: order.contactCompany,
        });

        setCompanyOrCharityID(order.companyOrCharityID);
        setMarketingOptOut(order.marketingOptOut);
      }
    });
  };

  const cancelOrder = () => {
    let payload = {
      orderStatus: 4,
    };

    updateOrder(id, payload, (responseBody) => {
      if (responseBody?.success) {
        navigate(`/orders/${id}`);
      } else {
        setApiError("Failed to update order");
      }
    });
  };

  const getRecipientDetails = (isCopyContact) => {
    return isCopyContact
      ? {
          recipientCompany: replaceWordChars(clientName),
          recipientName: replaceWordChars(contactName),
          recipientEmail: replaceWordChars(contactEmail),
          recipientLine1: replaceWordChars(contactLine1),
          recipientLine2: replaceWordChars(contactLine2),
          recipientTown: replaceWordChars(contactTown),
          recipientPostcode: replaceWordChars(contactPostcode),
          recipientCountry: recipientCountry,
          recipientTel: replaceWordChars(contactTel),
        }
      : {
          recipientCompany: replaceWordChars(recipientCompany),
          recipientName: replaceWordChars(recipientName),
          recipientEmail: replaceWordChars(recipientEmail),
          recipientLine1: replaceWordChars(recipientLine1),
          recipientLine2: replaceWordChars(recipientLine2),
          recipientTown: replaceWordChars(recipientTown),
          recipientPostcode: replaceWordChars(recipientPostcode),
          recipientCountry: recipientCountry,
          recipientTel: replaceWordChars(recipientTel),
        };
  };

  const save = () => {
    let _ok = true;
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // contactCompany (clientName)
    if (clientName === "") {
      _ok = false;
      setHasErrors(true);
      seterrClientName("Please enter an Organisation/Company Name");
    } else if (clientName.length > 100) {
      _ok = false;
      setHasErrors(true);
      seterrClientName("The maximum length of this field is 100 characters");
    }

    // contactName
    if (contactName === "") {
      _ok = false;
      setHasErrors(true);
      seterrContactName("Please enter the contact name");
    } else if (contactName.length > 100) {
      _ok = false;
      setHasErrors(true);
      seterrContactName("The maximum length of this field is 100 characters");
    }

    // contactEmail
    if (contactEmail === "") {
      _ok = false;
      setHasErrors(true);
      seterrContactEmail("Please enter the contact email");
    } else if (!emailRe.test(contactEmail)) {
      _ok = false;
      setHasErrors(true);
      seterrContactEmail("Please enter a valid email address");
    }

    // contactLine1
    if (contactLine1.length < 5) {
      _ok = false;
      setHasErrors(true);
      seterrContactLine1("Please enter the contact address");
    } else if (contactLine1.length > 100) {
      _ok = false;
      setHasErrors(true);
      seterrContactLine1("The maximum length of this field is 100 characters");
    }

    // contactLine2 (optional — only validate when provided)
    if (contactLine2 !== "") {
      if (contactLine2.length > 100) {
        _ok = false;
        setHasErrors(true);
        seterrContactLine2("The maximum length of this field is 100 characters");
      } else if (contactLine2.length < 5) {
        _ok = false;
        setHasErrors(true);
        seterrContactLine2("Please enter the contact address");
      }
    }

    // contactTown
    if (contactTown === "") {
      _ok = false;
      setHasErrors(true);
      seterrContactTown("Please enter the contact town");
    } else if (contactTown.length > 100) {
      _ok = false;
      setHasErrors(true);
      seterrContactTown("The maximum length of this field is 100 characters");
    }

    // contactPostcode
    if (contactPostcode.length < 5) {
      _ok = false;
      setHasErrors(true);
      seterrContactPostcode("Please enter the contact postcode");
    } else if (contactPostcode.length > 10) {
      _ok = false;
      setHasErrors(true);
      seterrContactPostcode("The maximum length of this field is 10 characters");
    }

    // contactTel
    if (!/^07\d{9}$/.test(contactTel)) {
      _ok = false;
      setHasErrors(true);
      seterrContactTel("Phone number must start with 07 and contain 11 digits");
    }

    if (!copyAddress) {
      // recipientName
      if (recipientName === "") {
        _ok = false;
        setHasErrors(true);
        seterrRecipientName("Please enter the recipient name");
      } else if (recipientName.length > 100) {
        _ok = false;
        setHasErrors(true);
        seterrRecipientName("The maximum length of this field is 100 characters");
      }

      // recipientCompany
      if (recipientCompany === "") {
        _ok = false;
        setHasErrors(true);
        seterrRecipientCompany("Please enter an Organisation/Company Name");
      } else if (recipientCompany.length > 100) {
        _ok = false;
        setHasErrors(true);
        seterrRecipientCompany("The maximum length of this field is 100 characters");
      }

      // recipientEmail
      if (recipientEmail === "") {
        _ok = false;
        setHasErrors(true);
        seterrRecipientEmail("Please enter the recipient email");
      } else if (!emailRe.test(recipientEmail)) {
        _ok = false;
        setHasErrors(true);
        seterrRecipientEmail("Please enter a valid email address");
      }

      // recipientLine1
      if (recipientLine1.length < 5) {
        _ok = false;
        setHasErrors(true);
        seterrRecipientLine1("Please enter the recipient address");
      } else if (recipientLine1.length > 100) {
        _ok = false;
        setHasErrors(true);
        seterrRecipientLine1("The maximum length of this field is 100 characters");
      }

      // recipientLine2 (optional — only validate when provided)
      if (recipientLine2 !== "") {
        if (recipientLine2.length > 100) {
          _ok = false;
          setHasErrors(true);
          seterrRecipientLine2("The maximum length of this field is 100 characters");
        } else if (recipientLine2.length < 5) {
          _ok = false;
          setHasErrors(true);
          seterrRecipientLine2("Please enter the recipient address");
        }
      }

      // recipientTown
      if (recipientTown === "") {
        _ok = false;
        setHasErrors(true);
        seterrRecipientTown("Please enter the recipient town");
      } else if (recipientTown.length > 100) {
        _ok = false;
        setHasErrors(true);
        seterrRecipientTown("The maximum length of this field is 100 characters");
      }

      // recipientPostcode
      if (recipientPostcode.length < 5) {
        _ok = false;
        setHasErrors(true);
        seterrRecipientPostcode("Please enter the recipient postcode");
      } else if (recipientPostcode.length > 10) {
        _ok = false;
        setHasErrors(true);
        seterrRecipientPostcode("The maximum length of this field is 10 characters");
      }

      // recipientTel
      if (!/^07\d{9}$/.test(recipientTel)) {
        _ok = false;
        setHasErrors(true);
        seterrRecipientTel("Phone number must start with 07 and contain 11 digits");
      }
    }

    if (orderItems.length === 0) {
      _ok = false;
      setHasErrors(true);
      seterrOrderItems("No products have been added to this order");
    }

    // hearAboutUs
    const hearAboutUsValue =
      howDidYouHear === "other" ? howDidYouHearOther.trim() : howDidYouHear;
    if (howDidYouHear === "") {
      _ok = false;
      setHasErrors(true);
      setErrHowDidYouHear("Please select how you heard about us");
    } else if (howDidYouHear === "other" && howDidYouHearOther.trim() === "") {
      _ok = false;
      setHasErrors(true);
      setErrHowDidYouHear("Please provide additional information for 'Other'");
    } else if (hearAboutUsValue.length < 2) {
      _ok = false;
      setHasErrors(true);
      setErrHowDidYouHear("Please provide additional information");
    } else if (hearAboutUsValue.length > 100) {
      _ok = false;
      setHasErrors(true);
      setErrHowDidYouHear("The maximum length of this field is 100 characters");
    }

    discountBrands.forEach(({ discountRate, setDiscountRateError }) => {
      if (isDiscountRateTooHigh(discountRate)) {
        _ok = false;
        setHasErrors(true);
        setDiscountRateError(DISCOUNT_RATE_ERROR);
      }
    });

    if (_ok) {
      setsaving(true);
      let payload = {
        clientPO: replaceWordChars(clientPO),
        contactName: replaceWordChars(contactName),
        contactLine1: replaceWordChars(contactLine1),
        contactLine2: replaceWordChars(contactLine2),
        contactTown: replaceWordChars(contactTown),
        contactPostcode: replaceWordChars(contactPostcode),
        contactCountry: contactCountry,
        contactEmail: replaceWordChars(contactEmail),
        contactTel: replaceWordChars(contactTel),
        copyAddress: copyAddress,
        ...getRecipientDetails(copyAddress),
        allowCardPayments: allowCardPayments,
        contactCompany: replaceWordChars(clientName),
        hearAboutUs:
          howDidYouHear === "other" ? howDidYouHearOther : howDidYouHear,
      };
      
      if (id === "new") {
        const orderLines = buildOrderLines(
          ORDER_OPERATION.CREATE,
          orderItems,
          products,
          orderNumber,
          brandDiscountState,
        );
        const overriddenDiscounts = discountBrands
          .filter(
            ({ isDiscountOverridden, discountTotal }) =>
              isDiscountOverridden && toNumber(discountTotal) > 0,
          )
          .map(({ brand, discountRate }) => ({
            discountRate: toNumber(discountRate),
            productBrand: brand,
          }));
        payload.orderLines = orderLines;
        payload.companyOrCharityID = companyOrCharityID;
        payload.marketingOptOut = marketingOptOut;

        if (overriddenDiscounts.length > 0) {
          payload.discounts = overriddenDiscounts;
        }
        
        if (_copy) {
          payload.sourceOrderId = _copy;
        }
        createOrder(payload, (responseBody) => {
          if (responseBody?.success) {
            navigate("/orders");
          } else {
            setApiError("Failed to create order");
          }
          setsaving(false);
        });
      } else {
        const currentOrderData = { ...payload };
        const updatedOrderPayload = getUpdatedOrderPayload(
          currentOrderData,
          originalOrderData,
        );
        if (orderItemsChanged) {
          const orderLines = buildOrderLines(
            ORDER_OPERATION.UPDATE,
            orderItems,
            products,
            orderNumber,
            brandDiscountState,
          );
          const deliveryAmount = Number(totalDeliveryAmount);
          updatedOrderPayload.addedDelivery = deliveryAmount !== 0;
          updatedOrderPayload.totalDeliveryAmount = deliveryAmount;
          updatedOrderPayload.orderLines = orderLines;
          updatedOrderPayload.hearAboutUs =
            howDidYouHear === "other" ? howDidYouHearOther : howDidYouHear;
        }
        updateOrder(id, updatedOrderPayload, (responseBody) => {
          if (responseBody?.success) {
            const historyPayload = {
              orderId: id,
              timeStamp: moment().toISOString(),
              comments: `Order updated by ${confirmedByName}`,
              dataType: "UPDATE",
            };
            updateOrderHistory(historyPayload, (historyResponse) => {
              if (historyResponse?.success) {
                navigate(`/orders/${id}`);
              } else {
                setApiError("Failed to update order history");
              }
            });
          } else {
            setApiError("Failed to update order");
          }
          setsaving(false);
        });
      }
    }
  };

  const addItem = () => {
    if (isDiscountLoading) return;
    const product = products.find((p) => p.value === type);
    if (!product) {
      seterrOrderItems("Please select a product type");
    } else if (Number.parseInt(qty) <= 0) {
      seterrOrderItems("Card Quantity must be greater than zero");
    } else if (product.productType === "card" && Number.parseFloat(value) < 0.01) {
      seterrOrderItems("Unit Value must be greater than zero");
    } else if (Number.parseFloat(value) > product.maxLoad) {
      seterrOrderItems(`The maximum Unit Value is £${product.maxLoad}`);
    } else {
      let newOrdersList = orderItems.concat([
        {
          ID: uuidv7(),
          productId: product.value,
          itemType: product.label,
          qty: qty,
          value: Number.parseFloat(value),
          brand: product.brand,
          typeFixedOrVariable: product.typeFixedOrVariable,
          deliveryType: product.deliveryType,
          productType: product.productType,
          addedWallets: false,
          addedDelivery: product.addDelivery,
        },
      ]);
      let deliveryAmount = totalDeliveryAmount;
      if (!deliveryChargeOverridden && totalDeliveryAmount === 0) {
        deliveryAmount = updateDeliveryAmount(newOrdersList);
      }
      setOrderItems(newOrdersList);
      setValue(0);
      setQty(0);
      setType("");
      setOrderItemsChanged(true);
      calculatedDiscounts(newOrdersList, deliveryAmount);
    }
  };

  // Updates delivery state and returns the new amount (state is not updated until next render).
  const updateDeliveryAmount = (orderItemsList) => {
    const hasPhysicalDelivery = orderItemsList.some((item) => {
      const product = products.find((p) => p.value === item.productId);
      return product?.deliveryType === "P";
    });

    if (hasPhysicalDelivery) {
      const deliveryProduct = products.find((p) => p.brand === "delivery");
      if (deliveryProduct?.amountFixed) {
        setTotalDeliveryAmount(deliveryProduct.amountFixed);
        return deliveryProduct.amountFixed;
      }
      return totalDeliveryAmount;
    }
    setTotalDeliveryAmount(0);
    return 0;
  };

  const deleteRow = (itemId) => {
    if (isDiscountLoading) return;
    let newOrdersList = orderItems.filter((item) => item.ID !== itemId);
    setOrderItems(newOrdersList);
    setOrderItemsChanged(true);
    let deliveryAmount = totalDeliveryAmount;
    if (!deliveryChargeOverridden) {
      deliveryAmount = updateDeliveryAmount(newOrdersList);
    }
    calculatedDiscounts(newOrdersList, deliveryAmount);
  };

  const sum = orderItems
    .map((v) => v.value * v.qty)
    .reduce((sum, current) => Number.parseFloat(sum) + Number.parseFloat(current), 0);

  function getTotalCost() {
    const totalDiscount = discountBrands.reduce(
      (acc, { discountAmount }) => acc + toNumber(discountAmount),
      0,
    );
    return sum - totalDiscount;
  }

  // Manual override of a brand's discount rate from the Discount section.
  const onChangeDiscountRate = (fieldName, value) => {
    const brandState = discountBrands.find((b) => b.fieldName === fieldName);
    const newAmount = brandState
      ? calcDiscountFromRate(brandState.discountTotal, value)
      : 0;
    if (brandState) {
      brandState.setDiscountRate(value);
      brandState.setIsCustomDiscount(true);
      brandState.setDiscountAmount(newAmount);
      brandState.setIsDiscountOverridden(true);
    }
    setOrderItemsChanged(true);
    const totalDiscount = discountBrands.reduce(
      (acc, { fieldName: f, discountAmount }) =>
        acc + (f === fieldName ? newAmount : toNumber(discountAmount)),
      0,
    );
    updateCardPaymentAvailability(sum - totalDiscount);
  };

  const onSelectProduct = (value) => {
    setType(value);
    const selectedProduct = products.find((product) => product.value === value);
    if (
      selectedProduct &&
      selectedProduct.typeFixedOrVariable === "F" &&
      selectedProduct.amountFixed >= 0
    ) {
      setValue(selectedProduct.amountFixed.toString());
      setIsCardValueVisible(false);
    } else {
      setValue(0);
      setIsCardValueVisible(true);
    }
  };

  const handleMarketingSourceChange = (name, value) => {
    setOrderItemsChanged(true);
    setHowDidYouHear(value);
    // Check if the value is "other" to show the additional input field
    if (value === "other") {
      setShowOtherInput(true);
      setHowDidYouHearOther("");
    } else {
      setShowOtherInput(false);
      setHowDidYouHearOther("");
    }
  };

  const handleOtherInputChange = (name, value) => {
    setHowDidYouHearOther(value);
  };

  function getUpdatedOrderPayload(current, original) {
    const updatedPayload = {};
    Object.keys(original).forEach((key) => {
      if (typeof current[key] === "object" && current[key] !== null) {
        if (JSON.stringify(current[key]) !== JSON.stringify(original[key])) {
          updatedPayload[key] = current[key];
        }
      } else {
        if (current[key] !== original[key]) {
          updatedPayload[key] = current[key];
        }
      }
    });
    return updatedPayload;
  }

  return (
    <div className="container-fluid">
      <div className="row row-eq-height">
        <div className="col-sm-12 content">
          <NarrowContainer>
            <BackButton
              url={id === "new" ? "/orders" : `/orders/${id}`}
              title="Back"
            />
            <Title title={id === "new" ? "New Order" : "Edit Order"} />

            <FormContainer
              title="Order Information"
              description={`Please add the details for this order.  The email address entered will receive an email requesting payment.`}
            >
              <div className="row">
                <div className="col-sm-6">
                  <TextInput
                    name="clientName"
                    type="text"
                    label="Client Name"
                    value={clientName}
                    maxLength={100}
                    asterix={true}
                    onChange={(name, value) => setClientName(value)}
                    error={errClientName}
                  />
                </div>
                <div className="col-sm-6">
                  <SwitchButton
                    label="Card Payments"
                    text="Allow Card payments?"
                    name="cardPayments"
                    value={allowCardPayments}
                    onChange={(name, value) => setAllowCardPayments(value)}
                    disabled={!isCardPaymentsEnable}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-sm-6">
                  <TextInput
                    name="contactName"
                    type="text"
                    label="Contact Name"
                    value={contactName}
                    maxLength={100}
                    asterix={true}
                    onChange={(name, value) => setContactName(value)}
                    error={errContactName}
                  />
                </div>
                <div className="col-sm-6">
                  <TextInput
                    name="clientPO"
                    type="text"
                    label="Client PO/Reference"
                    value={clientPO}
                    maxLength={25}
                    onChange={(name, value) => setClientPO(value)}
                    error={errClientPO}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-sm-6">
                  <TextInput
                    name="contactEmail"
                    type="email"
                    label="Client Email"
                    value={contactEmail}
                    maxLength={45}
                    asterix={true}
                    onChange={(name, value) => setContactEmail(value)}
                    error={errContactEmail}
                  />
                </div>
                <div className="col-sm-6">
                  <TextInput
                    name="contactTel"
                    type="tel"
                    label="Contact Telephone Number"
                    value={contactTel}
                    maxLength={11}
                    validation={"numeric"}
                    asterix={true}
                    onChange={(name, value) => setContactTel(value)}
                    error={errContactTel}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-sm-12">
                  <TextInput
                    name="contactLine1"
                    type="text"
                    label="Client Address"
                    placeholder="Address line 1"
                    value={contactLine1}
                    maxLength={100}
                    asterix={true}
                    onChange={(name, value) => setContactLine1(value)}
                    error={errContactLine1}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-sm-12">
                  <TextInput
                    name="contactLine2"
                    type="text"
                    placeholder="Address line 2"
                    value={contactLine2}
                    maxLength={100}
                    onChange={(name, value) => setContactLine2(value)}
                    error={errContactLine2}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-sm-12">
                  <TextInput
                    name="contactTown"
                    type="text"
                    placeholder="Town/City"
                    value={contactTown}
                    maxLength={100}
                    onChange={(name, value) => setContactTown(value)}
                    error={errContactTown}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-sm-6">
                  <TextInput
                    name="contactPostcode"
                    type="text"
                    placeholder="Post Code"
                    value={contactPostcode}
                    maxLength={10}
                    onChange={(name, value) => setContactPostcode(value)}
                    error={errContactPostcode}
                  />
                </div>
                <div className="col-sm-6">
                  <SelectBox
                    name="contactCountry"
                    label=""
                    placeholder=""
                    value={contactCountry}
                    maxLength={45}
                    options={[
                      {
                        value: "UK",
                        label: "United Kingdom",
                      },
                    ]}
                    disabled={true}
                    onChange={(name, value) => setContactCountry(value)}
                    error={errContactCountry}
                  />
                </div>
              </div>
            </FormContainer>

            <Divider />

            <FormContainer
              title="Order Items"
              description={`Please add the items for this order. Note any associated delivery charges will automatically be added once the order has been saved.`}
            >
              <div className="row">
                <div className="col-sm-12">
                  <table>
                    <thead>
                      <tr>
                        <th width="40%">Item Type</th>
                        <th width="25%">Quantity</th>
                        <th width="25%">Unit Value</th>
                        <th>&nbsp;</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderItems
                        .filter((d) => d.itemType !== 99)
                        .map((item) => (
                          <tr key={item.ID}>
                            <td>{item.itemType}</td>
                            <td>{item.qty}</td>
                            <td>&pound;{item.value.toFixed(2)}</td>
                            <td>
                              <div
                                className="delete-item"
                                style={
                                  isDiscountLoading
                                    ? { pointerEvents: "none", opacity: 0.4 }
                                    : undefined
                                }
                                onClick={(e) => {
                                  if (
                                    window.confirm(
                                      "Are you sure you wish to delete this item?",
                                    )
                                  )
                                    deleteRow(item.ID);
                                }}
                              >
                                <i className="far fa-times-circle"></i>
                              </div>
                            </td>
                          </tr>
                        ))}

                      <tr style={{ borderBottom: 0 }}>
                        <td
                          colSpan={2}
                          style={{ paddingBottom: 0, textAlign: "right" }}
                        >
                          Cost:
                        </td>
                        <td>&pound;{sum.toFixed(2)}</td>
                        <td>&nbsp;</td>
                      </tr>

                      {orderItems.length > 0 && totalDeliveryAmount > 0 && (
                        <tr style={{ borderBottom: 0 }}>
                          <td
                            colSpan={2}
                            style={{ paddingBottom: 0, textAlign: "right" }}
                          >
                            Delivery Charge:
                          </td>
                          <td>
                            &pound;{Number.parseFloat(totalDeliveryAmount).toFixed(2)}
                          </td>
                          <td>&nbsp;</td>
                        </tr>
                      )}
                      {discountBrands
                        .filter(
                          ({ discountRate, discountTotal }) =>
                            toNumber(discountRate) > 0 && toNumber(discountTotal) > 0,
                        )
                        .map(({ brand, label, discountRate, discountAmount }) => (
                          <tr key={brand} style={{ borderBottom: 0 }}>
                            <td
                              colSpan={2}
                              style={{ paddingBottom: 0, textAlign: "right" }}
                            >
                              {label} Discount (
                              {toNumber(discountRate).toFixed(2)}%):
                            </td>
                            <td>&pound;{toNumber(discountAmount).toFixed(2)}</td>
                            <td>&nbsp;</td>
                          </tr>
                        ))}
                      {orderItems.length > 0 && (
                        <tr style={{ borderBottom: 0 }}>
                          <td
                            colSpan={2}
                            style={{ paddingBottom: 10, textAlign: "right" }}
                          >
                            <strong>Total Cost:</strong>
                          </td>
                          <td style={{ paddingBottom: 10 }}>
                            <strong>
                              &pound;
                              {(getTotalCost() + toNumber(totalDeliveryAmount)).toFixed(2)}
                            </strong>
                          </td>
                          <td>&nbsp;</td>
                        </tr>
                      )}

                      {orderItems.length === 0 && (
                        <tr>
                          <td
                            colSpan={4}
                            className="text-cell"
                            style={{ paddingTop: 10, paddingBottom: 10 }}
                          >
                            No items added.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="row">
                <div className="col-sm-6">
                  <SelectBox
                    name="type"
                    label="Product Type"
                    placeholder="Select..."
                    value={type}
                    maxLength={45}
                    asterix={true}
                    options={products.filter(
                      (product) => product.value !== "DELIVERY",
                    )}
                    onChange={(name, value) => onSelectProduct(value)}
                    disabled={isDiscountLoading}
                  />
                </div>
                <div className="col-sm-3">
                  {type !== "" && (
                    <TextInput
                      name="qty"
                      type="text"
                      placeholder=""
                      label="Card Quantity"
                      value={qty}
                      asterix={true}
                      pattern={"[0-9]"}
                      onChange={(name, value) => setQty(value)}
                    />
                  )}
                </div>
                <div className="col-sm-3">
                  {type !== "" && isCardValueVisible && (
                    <CurrencyInput
                      name="value"
                      type="text"
                      placeholder=""
                      label="Unit Value"
                      value={value}
                      asterix={true}
                      format="currency"
                      onChange={(name, value) => setValue(value)}
                    />
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-sm-6">
                  {errOrderItems && (
                    <span className="invalid-error">{errOrderItems}</span>
                  )}
                </div>
                <div className="col-sm-6 text-right">
                  {((type !== "" && type !== 0) || isDiscountLoading) && (
                    <SubmitButton
                      text={
                        isDiscountLoading ? (
                          <i
                            className="fas fa-spinner fa-spin"
                            aria-label="Calculating discounts"
                          />
                        ) : (
                          "Add to Order"
                        )
                      }
                      className="btn btn-secondary"
                      onClick={addItem}
                      disabled={isDiscountLoading}
                    />
                  )}
                </div>
              </div>
            </FormContainer>

            <Divider />

            <FormContainer
              title="Discount"
              description={`Please add any associated discount for this order.`}
            >
              <div className="row">
                {discountBrands.map(
                  ({
                    brand,
                    label,
                    fieldName,
                    discountRate,
                    isDiscountEditable,
                    isDiscountOverridden,
                    discountRateError,
                  }) => (
                    <div className="col-sm-6" key={brand}>
                      <CurrencyInput
                        name={fieldName}
                        placeholder=""
                        label={`${label} Discount Percentage`}
                        value={discountRate}
                        asterix={true}
                        format="currency"
                        onChange={onChangeDiscountRate}
                        disabled={!isDiscountEditable}
                        error={
                          isDiscountRateTooHigh(discountRate)
                            ? DISCOUNT_RATE_ERROR
                            : discountRateError
                        }
                      />
                      {isDiscountOverridden && (
                        <div style={{ marginTop: 2 }}>
                          <span
                            style={{
                              color: "red",
                              fontSize: "14px",
                              fontWeight: "bold",
                            }}
                          >
                            Warning: {label} Discount has been overridden
                          </span>
                        </div>
                      )}
                    </div>
                  ),
                )}
              </div>
              <div className="row">
                <div className="col-sm-6">
                  {id !== "new" && (
                    <CurrencyInput
                      name="deliveryChargeOverride"
                      type="text"
                      placeholder=""
                      label="Delivery Charge Override"
                      value={totalDeliveryAmount}
                      asterix={false}
                      format="currency"
                      onChange={(name, value) => {
                        setTotalDeliveryAmount(value);
                        setDeliveryChargeOverridden(true);
                        setOrderItemsChanged(true);
                        updateCardPaymentAvailability(getTotalCost(), value);
                      }}
                    />
                  )}
                </div>
              </div>
            </FormContainer>

            <Divider />

            <FormContainer
              title="Gift Card/Voucher Recipient"
              description={`Please confirm where the gift cards/vouchers should be sent to.`}
            >
              <div className="row">
                <div className="col-sm-6">
                  <SwitchButton
                    label="Recipient Details"
                    text="Copy as above?"
                    name="copyAddress"
                    value={copyAddress}
                    onChange={(name, value) => setCopyAddress(value)}
                  />
                </div>
              </div>
              {!copyAddress && (
                <div>
                  <div className="row">
                    <div className="col-sm-6">
                      <TextInput
                        name="recipientName"
                        type="text"
                        label="Recipient Name"
                        value={recipientName}
                        maxLength={100}
                        asterix={true}
                        onChange={(name, value) => setRecipientName(value)}
                        error={errRecipientName}
                      />
                    </div>
                    <div className="col-sm-6">
                      <TextInput
                        name="recipientCompany"
                        type="text"
                        label="Organisation/Company Name"
                        value={recipientCompany}
                        maxLength={100}
                        asterix={true}
                        onChange={(name, value) => setRecipientCompany(value)}
                        error={errRecipientCompany}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-6">
                      <TextInput
                        name="recipientEmail"
                        type="email"
                        label="Recipient Email Address"
                        value={recipientEmail}
                        maxLength={45}
                        asterix={true}
                        onChange={(name, value) => setRecipientEmail(value)}
                        error={errRecipientEmail}
                      />
                    </div>
                    <div className="col-sm-6">
                      <TextInput
                        name="recipientTel"
                        type="tel"
                        label="Recipient Telephone Number"
                        value={recipientTel}
                        maxLength={11}
                        validation={"numeric"}
                        asterix={true}
                        onChange={(name, value) => setRecipientTel(value)}
                        error={errRecipientTel}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-12">
                      <TextInput
                        name="recipientLine1"
                        type="text"
                        label="Recipient Address"
                        placeholder="Address line 1"
                        value={recipientLine1}
                        maxLength={100}
                        asterix={true}
                        onChange={(name, value) => setRecipientLine1(value)}
                        error={errRecipientLine1}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-12">
                      <TextInput
                        name="recipientLine2"
                        type="text"
                        placeholder="Address line 2"
                        value={recipientLine2}
                        maxLength={100}
                        onChange={(name, value) => setRecipientLine2(value)}
                        error={errRecipientLine2}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-12">
                      <TextInput
                        name="recipientTown"
                        type="text"
                        placeholder="Town/City"
                        value={recipientTown}
                        maxLength={100}
                        onChange={(name, value) => setRecipientTown(value)}
                        error={errRecipientTown}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-6">
                      <TextInput
                        name="recipientPostcode"
                        type="text"
                        placeholder="Post Code"
                        value={recipientPostcode}
                        maxLength={10}
                        onChange={(name, value) => setRecipientPostcode(value)}
                        error={errRecipientPostcode}
                      />
                    </div>
                    <div className="col-sm-6">
                      <SelectBox
                        name="recipientCountry"
                        label=""
                        placeholder="Select Country"
                        value={recipientCountry}
                        maxLength={45}
                        options={[
                          {
                            value: "UK",
                            label: "United Kingdom",
                          },
                        ]}
                        disabled={true}
                        onChange={(name, value) => setRecipientCountry(value)}
                        error={errRecipientCountry}
                      />
                    </div>
                  </div>
                </div>
              )}
            </FormContainer>

            <Divider />

            <FormContainer>
              <div className="row">
                <div className="col-sm-6">
                  <SelectBox
                    name="howDidYouHear"
                    label="How did you hear about us?"
                    placeholder="Select an option"
                    value={howDidYouHear}
                    maxLength={100}
                    asterix={true}
                    options={marketingSources}
                    onChange={(name, value) =>
                      handleMarketingSourceChange(name, value)
                    }
                    error={errHowDidYouHear}
                  />
                </div>
                {showOtherInput && (
                  <div className="col-sm-6">
                    <TextInput
                      name="howDidYouHearOther"
                      type="text"
                      label="Other - Please provide information*"
                      value={howDidYouHearOther}
                      maxLength={100}
                      asterix={true}
                      onChange={(name, value) =>
                        handleOtherInputChange(name, value)
                      }
                    />
                  </div>
                )}
              </div>
            </FormContainer>
            <div className="row">
              <div className="col-sm-4">
                {id !== "new" && (
                  <SubmitButton
                    text="Cancel Order"
                    className="btn btn-secondary"
                    onClick={(e) => {
                      if (
                        window.confirm(
                          "Are you sure you wish to cancel this order?",
                        )
                      )
                        cancelOrder();
                    }}
                  />
                )}
              </div>
              <div
                className="col-sm-4"
                style={{ paddingBottom: 60, textAlign: "center" }}
              >
                {apiError === "failed" ? (
                  <span
                    className="invalid-error"
                    style={{ textAlign: "center" }}
                  >
                    There has been a problem continuing with your order. Please
                    wait a while and try again.
                  </span>
                ) : apiError ? (
                  <span className="invalid-error">{apiError}</span>
                ) : hasErrors ? (
                  <span className="invalid-error">
                    Please check the errors above
                  </span>
                ) : (
                  ""
                )}
              </div>

              <div
                className="col-sm-4 text-right"
                style={{ paddingBottom: 60 }}
              >
                <SubmitButton
                  text={
                    saving
                      ? "Saving..."
                      : id === "new"
                        ? "Create Order"
                        : "Update Order"
                  }
                  className="btn"
                  onClick={save}
                  disabled={saving || isDiscountLoading}
                />
              </div>
            </div>
          </NarrowContainer>
        </div>
      </div>
    </div>
  );
}

export default OrderEditPage;
